import React, { useState, useEffect } from 'react';
import { getStoredAuthHeader, storeAuthCredentials, clearAuthCredentials, createBasicAuthHeader } from '../services/auth-storage';
import { testAuth, getAllGuests, getStats, createGuest, updateGuest, deleteGuest, importGuests, exportGuests } from '../services/admin-api';
import { GuestData } from '../types';
import type { Stats } from '../services/admin-api';
import '../styles/admin.css';

const AdminPage: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(true);

  // Admin data
  const [guests, setGuests] = useState<GuestData[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState('');

  // Form states
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingGuest, setEditingGuest] = useState<GuestData | null>(null);
  const [formData, setFormData] = useState<Partial<GuestData>>({});

  // Filter and search
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'yes' | 'no'>('all');
  const [sortField, setSortField] = useState<keyof GuestData>('guest');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Import/Export states
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileColumns, setFileColumns] = useState<string[]>([]);
  const [columnMapping, setColumnMapping] = useState({
    code: '',
    seat: '',
    guest: '',
    attendanceStatus: '',
    confirmedSeats: '',
    confirmationDate: '',
    kidsSeats: '',
    confirmedKidsSeats: '',
  });
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);

  // WhatsApp copy state
  const [copiedGuestCode, setCopiedGuestCode] = useState<string | null>(null);

  // Check for stored auth on mount
  useEffect(() => {
    const authHeader = getStoredAuthHeader();
    if (authHeader) {
      setIsAuthenticated(true);
      loadAdminData(authHeader);
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setLoading(true);

    const isValid = await testAuth(username, password);
    if (isValid) {
      storeAuthCredentials(username, password);
      setIsAuthenticated(true);
      const authHeader = createBasicAuthHeader(username, password);
      await loadAdminData(authHeader);
    } else {
      setAuthError('Invalid credentials');
    }
    setLoading(false);
  };

  const handleLogout = () => {
    clearAuthCredentials();
    setIsAuthenticated(false);
    setGuests([]);
    setStats(null);
  };

  const loadAdminData = async (authHeader?: string) => {
    const header = authHeader || getStoredAuthHeader();
    if (!header) return;

    setLoadingData(true);
    setError('');
    try {
      const [guestsData, statsData] = await Promise.all([
        getAllGuests(header),
        getStats(header),
      ]);
      setGuests(guestsData);
      setStats(statsData);
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoadingData(false);
      setLoading(false);
    }
  };

  const handleCreateGuest = async (e: React.FormEvent) => {
    e.preventDefault();
    const authHeader = getStoredAuthHeader();
    if (!authHeader) return;

    try {
      await createGuest(authHeader, formData);
      await loadAdminData();
      setShowCreateForm(false);
      setFormData({});
    } catch (err: any) {
      alert(err.message || 'Failed to create guest');
    }
  };

  const handleUpdateGuest = async (e: React.FormEvent) => {
    e.preventDefault();
    const authHeader = getStoredAuthHeader();
    if (!authHeader || !editingGuest) return;

    try {
      await updateGuest(authHeader, editingGuest.code, formData);
      await loadAdminData();
      setEditingGuest(null);
      setFormData({});
    } catch (err: any) {
      alert(err.message || 'Failed to update guest');
    }
  };

  const handleDeleteGuest = async (code: string) => {
    if (!window.confirm('Are you sure you want to delete this guest?')) return;

    const authHeader = getStoredAuthHeader();
    if (!authHeader) return;

    try {
      await deleteGuest(authHeader, code);
      await loadAdminData();
    } catch (err: any) {
      alert(err.message || 'Failed to delete guest');
    }
  };

  const handleExport = async () => {
    const authHeader = getStoredAuthHeader();
    if (!authHeader) return;

    setExporting(true);
    try {
      const blob = await exportGuests(authHeader);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `guests-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.message || 'Failed to export guests');
    } finally {
      setExporting(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      alert('Please select a CSV file');
      return;
    }

    setSelectedFile(file);

    // Read CSV file to extract column headers
    try {
      const reader = new FileReader();
      reader.onload = (event) => {
        const data = event.target?.result;
        if (typeof data === 'string') {
          const lines = data.split('\n');
          if (lines.length > 0) {
            // Parse CSV header line respecting quotes
            const headerLine = lines[0];
            const headers: string[] = [];
            let current = '';
            let inQuotes = false;

            for (let i = 0; i < headerLine.length; i++) {
              const char = headerLine[i];
              if (char === '"') {
                inQuotes = !inQuotes;
              } else if (char === ',' && !inQuotes) {
                headers.push(current.trim());
                current = '';
              } else {
                current += char;
              }
            }
            headers.push(current.trim());

            setFileColumns(headers);
          }
        }
      };
      reader.readAsText(file);
    } catch (err) {
      console.error('Error reading file:', err);
      alert('Error reading file');
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;
    if (!columnMapping.code || !columnMapping.seat || !columnMapping.guest) {
      alert('Please map required fields: Code, Guest, and Adult Seats');
      return;
    }

    if (!window.confirm('WARNING: This will DELETE ALL existing guests and replace them with the imported data. Are you sure?')) {
      return;
    }

    const authHeader = getStoredAuthHeader();
    if (!authHeader) return;

    setImporting(true);
    try {
      const result = await importGuests(authHeader, selectedFile, {
        code: columnMapping.code,
        seat: columnMapping.seat,
        guest: columnMapping.guest,
        attendanceStatus: columnMapping.attendanceStatus || undefined,
        confirmedSeats: columnMapping.confirmedSeats || undefined,
        confirmationDate: columnMapping.confirmationDate || undefined,
        kidsSeats: columnMapping.kidsSeats || undefined,
        confirmedKidsSeats: columnMapping.confirmedKidsSeats || undefined,
      });
      alert(`Successfully imported ${result.importedCount} guests`);
      setShowImportModal(false);
      setSelectedFile(null);
      setFileColumns([]);
      setColumnMapping({
        code: '',
        seat: '',
        guest: '',
        attendanceStatus: '',
        confirmedSeats: '',
        confirmationDate: '',
        kidsSeats: '',
        confirmedKidsSeats: '',
      });
      await loadAdminData();
    } catch (err: any) {
      alert(err.message || 'Failed to import guests');
    } finally {
      setImporting(false);
    }
  };

  const copyWhatsAppMessage = async (guestCode: string, guestName: string) => {
    try {
      const weddingConfig = require('../data/wedding-config.json');
      const { whatsappTemplate, siteUrl } = weddingConfig;

      if (!whatsappTemplate || !siteUrl) {
        alert('WhatsApp template not configured');
        return;
      }

      // Generate personalized link
      const personalizedLink = `${siteUrl}/?code=${guestCode}`;

      // Replace {LINK} and {GUEST} placeholders
      const message = whatsappTemplate
        .replace('{LINK}', personalizedLink)
        .replace('{GUEST}', guestName);

      // Copy to clipboard
      await navigator.clipboard.writeText(message);

      // Show success feedback
      setCopiedGuestCode(guestCode);
      setTimeout(() => setCopiedGuestCode(null), 3000);
    } catch (err) {
      console.error('Failed to copy WhatsApp message:', err);
      alert('Failed to copy message to clipboard');
    }
  };

  const openInvitationLink = (guestCode: string) => {
    try {
      const weddingConfig = require('../data/wedding-config.json');
      const { siteUrl } = weddingConfig;

      if (!siteUrl) {
        alert('Site URL not configured');
        return;
      }

      // Generate personalized link
      const personalizedLink = `${siteUrl}/?code=${guestCode}`;

      // Open in new window
      window.open(personalizedLink, '_blank');
    } catch (err) {
      console.error('Failed to open invitation link:', err);
      alert('Failed to open invitation link');
    }
  };

  const filteredAndSortedGuests = guests
    .filter((guest) => {
      if (filterStatus !== 'all' && guest.attendanceStatus !== filterStatus) {
        return false;
      }
      if (searchTerm && !guest.guest.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !guest.code.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];

      // Handle undefined values
      if (aVal === undefined && bVal === undefined) return 0;
      if (aVal === undefined) return 1;
      if (bVal === undefined) return -1;

      const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return sortDirection === 'asc' ? comparison : -comparison;
    });

  if (loading) {
    return (
      <div className="admin-container">
        <div className="admin-loading">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="admin-container">
        <div className="admin-login-card">
          <h1 className="admin-title">Admin Login</h1>
          <form onSubmit={handleLogin} className="admin-login-form">
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoComplete="username"
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>
            {authError && <div className="error-message">{authError}</div>}
            <button type="submit" className="btn-primary">Login</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1 className="admin-title">Wedding Guest Management</h1>
        <button onClick={handleLogout} className="btn-secondary">Logout</button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Stats Dashboard */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Total Guests</div>
            <div className="stat-value">{stats.totalGuests}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Confirmed</div>
            <div className="stat-value confirmed">{stats.totalConfirmed}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Declined</div>
            <div className="stat-value declined">{stats.totalDeclined}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Pending</div>
            <div className="stat-value pending">{stats.totalPending}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Adult Seats Available</div>
            <div className="stat-value">{stats.totalSeatsAvailable}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Adult Seats Confirmed</div>
            <div className="stat-value confirmed">{stats.totalConfirmedSeats}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Kids Seats Available</div>
            <div className="stat-value">{stats.totalKidsSeatsAvailable}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Kids Seats Confirmed</div>
            <div className="stat-value confirmed">{stats.totalConfirmedKidsSeats}</div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="admin-controls">
        <button onClick={() => {setShowCreateForm(true); setFormData({});}} className="btn-primary">
          + Add Guest
        </button>
        <button onClick={() => loadAdminData()} className="btn-secondary" disabled={loadingData}>
          {loadingData ? 'Refreshing...' : 'Refresh'}
        </button>
        <button onClick={handleExport} className="btn-secondary" disabled={exporting || guests.length === 0}>
          {exporting ? 'Exporting...' : 'Export to CSV'}
        </button>
        <button onClick={() => setShowImportModal(true)} className="btn-secondary">
          Import from CSV
        </button>

        <div className="search-filter-container">
          <input
            type="text"
            placeholder="Search by name or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="yes">Confirmed</option>
            <option value="no">Declined</option>
          </select>
        </div>
      </div>

      {/* Guest Table */}
      <div className="table-container">
        <table className="guests-table">
          <thead>
            <tr>
              <th onClick={() => {setSortField('code'); setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');}}>
                Code {sortField === 'code' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => {setSortField('guest'); setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');}}>
                Name {sortField === 'guest' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => {setSortField('seat'); setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');}}>
                Adult Seats {sortField === 'seat' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => {setSortField('kidsSeats'); setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');}}>
                Kids Seats {sortField === 'kidsSeats' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => {setSortField('attendanceStatus'); setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');}}>
                Status {sortField === 'attendanceStatus' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th>Confirmed Adults</th>
              <th>Confirmed Kids</th>
              <th>Confirmation Date</th>
              <th>View</th>
              <th>WhatsApp</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedGuests.map((guest) => (
              <tr key={guest.code}>
                <td>{guest.code}</td>
                <td>{guest.guest}</td>
                <td>{guest.seat}</td>
                <td>{guest.kidsSeats || 0}</td>
                <td>
                  <span className={`status-badge status-${guest.attendanceStatus}`}>
                    {guest.attendanceStatus}
                  </span>
                </td>
                <td>{guest.confirmedSeats || '-'}</td>
                <td>{guest.confirmedKidsSeats || '-'}</td>
                <td>{guest.confirmationDate ? new Date(guest.confirmationDate).toLocaleString() : '-'}</td>
                <td className="view-cell">
                  <button
                    onClick={() => openInvitationLink(guest.code)}
                    className="btn-sm btn-view"
                    title="Open invitation link in new window"
                  >
                    <span className="view-icon">🔗</span> View
                  </button>
                </td>
                <td className="whatsapp-cell">
                  <button
                    onClick={() => copyWhatsAppMessage(guest.code, guest.guest)}
                    className="btn-sm btn-whatsapp"
                    title="Copy WhatsApp invitation message"
                  >
                    {copiedGuestCode === guest.code ? (
                      <>
                        <span className="whatsapp-icon">✓</span> Copied!
                      </>
                    ) : (
                      <>
                        <span className="whatsapp-icon">💬</span> Copy
                      </>
                    )}
                  </button>
                </td>
                <td className="actions-cell">
                  <button onClick={() => {setEditingGuest(guest); setFormData(guest);}} className="btn-sm btn-edit">
                    Edit
                  </button>
                  <button onClick={() => handleDeleteGuest(guest.code)} className="btn-sm btn-delete">
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="table-footer">
          Showing {filteredAndSortedGuests.length} of {guests.length} guests
        </div>
      </div>

      {/* Create/Edit Form Modal */}
      {(showCreateForm || editingGuest) && (
        <div className="modal-overlay" onClick={() => {setShowCreateForm(false); setEditingGuest(null); setFormData({});}}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingGuest ? 'Edit Guest' : 'Create Guest'}</h2>
            <form onSubmit={editingGuest ? handleUpdateGuest : handleCreateGuest}>
              <div className="form-group">
                <label>Code*</label>
                <input
                  type="text"
                  value={formData.code || ''}
                  onChange={(e) => setFormData({...formData, code: e.target.value})}
                  required
                  disabled={!!editingGuest}
                />
              </div>
              <div className="form-group">
                <label>Guest Name*</label>
                <input
                  type="text"
                  value={formData.guest || ''}
                  onChange={(e) => setFormData({...formData, guest: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Adult Seats*</label>
                <input
                  type="number"
                  min="1"
                  value={formData.seat || ''}
                  onChange={(e) => setFormData({...formData, seat: parseInt(e.target.value)})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Kids Seats</label>
                <input
                  type="number"
                  min="0"
                  value={formData.kidsSeats || 0}
                  onChange={(e) => setFormData({...formData, kidsSeats: parseInt(e.target.value)})}
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  value={formData.attendanceStatus || 'pending'}
                  onChange={(e) => setFormData({...formData, attendanceStatus: e.target.value as any})}
                >
                  <option value="pending">Pending</option>
                  <option value="yes">Confirmed</option>
                  <option value="no">Declined</option>
                </select>
              </div>
              {formData.attendanceStatus === 'yes' && (
                <>
                  <div className="form-group">
                    <label>Confirmed Adults</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.confirmedSeats || ''}
                      onChange={(e) => setFormData({...formData, confirmedSeats: parseInt(e.target.value)})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Confirmed Kids</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.confirmedKidsSeats || 0}
                      onChange={(e) => setFormData({...formData, confirmedKidsSeats: parseInt(e.target.value)})}
                    />
                  </div>
                </>
              )}
              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  {editingGuest ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => {setShowCreateForm(false); setEditingGuest(null); setFormData({});}}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="modal-overlay" onClick={() => {setShowImportModal(false); setSelectedFile(null); setFileColumns([]);}}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Import Guests from CSV</h2>
            <div style={{ marginBottom: '20px', padding: '15px', background: '#fff3cd', borderRadius: '4px', color: '#856404' }}>
              <strong>Warning:</strong> This will DELETE ALL existing guests and replace them with data from the file.
            </div>

            {!selectedFile ? (
              <div className="form-group">
                <label>Select CSV File</label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                />
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label>Selected File: {selectedFile.name}</label>
                </div>

                <h3 style={{ marginTop: '20px' }}>Map Columns</h3>
                <p style={{ fontSize: '14px', color: '#666' }}>
                  Match your file columns to the guest fields. Required fields are marked with *
                </p>

                <div className="form-group">
                  <label>Code* (Unique identifier)</label>
                  <select
                    value={columnMapping.code}
                    onChange={(e) => setColumnMapping({...columnMapping, code: e.target.value})}
                    required
                  >
                    <option value="">-- Select Column --</option>
                    {fileColumns.map((col) => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Guest Name*</label>
                  <select
                    value={columnMapping.guest}
                    onChange={(e) => setColumnMapping({...columnMapping, guest: e.target.value})}
                    required
                  >
                    <option value="">-- Select Column --</option>
                    {fileColumns.map((col) => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Adult Seats*</label>
                  <select
                    value={columnMapping.seat}
                    onChange={(e) => setColumnMapping({...columnMapping, seat: e.target.value})}
                    required
                  >
                    <option value="">-- Select Column --</option>
                    {fileColumns.map((col) => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Kids Seats (optional)</label>
                  <select
                    value={columnMapping.kidsSeats}
                    onChange={(e) => setColumnMapping({...columnMapping, kidsSeats: e.target.value})}
                  >
                    <option value="">-- Select Column --</option>
                    {fileColumns.map((col) => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Status (optional)</label>
                  <select
                    value={columnMapping.attendanceStatus}
                    onChange={(e) => setColumnMapping({...columnMapping, attendanceStatus: e.target.value})}
                  >
                    <option value="">-- Select Column --</option>
                    {fileColumns.map((col) => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Confirmed Adults (optional)</label>
                  <select
                    value={columnMapping.confirmedSeats}
                    onChange={(e) => setColumnMapping({...columnMapping, confirmedSeats: e.target.value})}
                  >
                    <option value="">-- Select Column --</option>
                    {fileColumns.map((col) => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Confirmed Kids (optional)</label>
                  <select
                    value={columnMapping.confirmedKidsSeats}
                    onChange={(e) => setColumnMapping({...columnMapping, confirmedKidsSeats: e.target.value})}
                  >
                    <option value="">-- Select Column --</option>
                    {fileColumns.map((col) => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Confirmation Date (optional)</label>
                  <select
                    value={columnMapping.confirmationDate}
                    onChange={(e) => setColumnMapping({...columnMapping, confirmationDate: e.target.value})}
                  >
                    <option value="">-- Select Column --</option>
                    {fileColumns.map((col) => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                </div>
              </>
            )}

            <div className="form-actions">
              {selectedFile && (
                <button
                  type="button"
                  onClick={handleImport}
                  className="btn-primary"
                  disabled={importing || !columnMapping.code || !columnMapping.seat || !columnMapping.guest}
                >
                  {importing ? 'Importing...' : 'Import'}
                </button>
              )}
              <button
                type="button"
                onClick={() => {setShowImportModal(false); setSelectedFile(null); setFileColumns([]);}}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
